import React, { useEffect, useState } from 'react';
import api from '../api/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ShieldCheck, PaperPlaneTilt, ArrowClockwise, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';

const LEVEL_STYLES = {
  full: 'bg-emerald-100 text-emerald-800',
  operator: 'bg-sky-100 text-sky-800',
  readonly: 'bg-amber-100 text-amber-800',
};

const STATUS_LABELS = { pending: 'In attesa', accepted: 'Accettato', expired: 'Scaduto' };
const STATUS_STYLES = {
  pending: 'bg-sky-100 text-sky-800',
  accepted: 'bg-emerald-100 text-emerald-800',
  expired: 'bg-neutral-200 text-neutral-700',
};

/** Sezione Strumenti: creazione e livelli degli amministratori. */
export const AdminAccountsPanel = () => {
  const [admins, setAdmins] = useState([]);
  const [levels, setLevels] = useState([]);
  const [myLevel, setMyLevel] = useState('full');
  const [invites, setInvites] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', admin_level: 'full' });
  const [busy, setBusy] = useState(false);

  const load = () => Promise.all([
    api.get('/api/admin/admins').then((r) => {
      setAdmins(r.data.items || []);
      setLevels(r.data.levels || []);
      setMyLevel(r.data.my_level || 'full');
    }),
    api.get('/api/admin/admins/invites').then((r) => setInvites(r.data.items || [])).catch(() => {}),
  ]).catch(() => {});

  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Compila nome e email');
      return;
    }
    setBusy(true);
    try {
      const r = await api.post('/api/admin/admins/invites', form);
      toast.success(r.data.email_sent
        ? `Invito inviato a ${form.email}`
        : 'Invito creato: email non consegnata, copia il link e invialo manualmente');
      setForm({ name: '', email: '', admin_level: 'full' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore invio invito');
    } finally { setBusy(false); }
  };

  const resend = async (inviteId) => {
    try {
      const r = await api.post(`/api/admin/admins/invites/${inviteId}/resend`);
      toast.success(r.data.email_sent ? 'Invito rinviato' : 'Nuovo link generato (email non consegnata)');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore rinvio invito');
    }
  };

  const revoke = async (inviteId) => {
    try {
      await api.delete(`/api/admin/admins/invites/${inviteId}`);
      toast.success('Invito revocato');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore revoca invito');
    }
  };

  const changeLevel = async (userId, level) => {
    try {
      await api.put(`/api/admin/admins/${userId}/level`, { admin_level: level });
      toast.success('Livello aggiornato');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore aggiornamento livello');
    }
  };

  const canManage = myLevel === 'full';

  return (
    <div className="border border-border rounded-xl p-5" data-testid="admin-accounts-panel">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck size={22} className="text-primary" weight="duotone" />
        <h3 className="font-heading font-medium text-lg">Amministratori e livelli</h3>
      </div>
      <p className="text-sm text-foreground/70 mb-4">
        <strong>Gestione completa</strong>: accesso a tutto. <strong>Operativo</strong>: gestisce richieste,
        professionisti e comunicazioni ma non impostazioni, tassonomia, pulizia dati o creazione admin.{' '}
        <strong>Solo lettura</strong>: consultazione ed export, nessuna modifica.
      </p>

      {canManage ? (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome e cognome" data-testid="new-admin-name" />
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@vivalink.it" data-testid="new-admin-email" />
            <Select value={form.admin_level} onValueChange={(v) => setForm({ ...form, admin_level: v })}>
              <SelectTrigger data-testid="new-admin-level"><SelectValue placeholder="Livello" /></SelectTrigger>
              <SelectContent>
                {levels.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={invite} disabled={busy} className="rounded-full mt-4" data-testid="invite-admin-btn">
            <PaperPlaneTilt size={15} className="mr-1.5" /> Invia invito
          </Button>
          <p className="text-xs text-foreground/55 mt-2">
            L'invitato riceve un'email con un link valido 48 ore e imposta da sé la password. Nessun account
            viene creato finché l'invito non viene accettato.
          </p>
        </>
      ) : (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="admin-panel-restricted">
          Il tuo livello non consente di creare o modificare gli amministratori.
        </p>
      )}

      <div className="mt-5 space-y-2">
        <p className="text-xs uppercase tracking-wider text-foreground/55">Amministratori attivi ({admins.length})</p>        {admins.map((a) => (
          <div key={a.user_id} className="flex flex-wrap items-center justify-between gap-3 border border-border rounded-lg px-3 py-2 text-sm" data-testid={`admin-row-${a.user_id}`}>
            <div className="flex-1 min-w-[180px]">
              <p className="font-medium">{a.name}</p>
              <p className="text-foreground/60 text-xs">{a.email}</p>
            </div>
            <Badge className={LEVEL_STYLES[a.admin_level] || ''} data-testid={`admin-level-badge-${a.user_id}`}>
              {a.admin_level_label}
            </Badge>
            {canManage && (
              <Select value={a.admin_level} onValueChange={(v) => changeLevel(a.user_id, v)}>
                <SelectTrigger className="w-44 h-9" data-testid={`admin-level-select-${a.user_id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {levels.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        ))}
      </div>

      {invites.length > 0 && (
        <div className="mt-6 space-y-2" data-testid="admin-invites-list">
          <p className="text-xs uppercase tracking-wider text-foreground/55">Inviti ({invites.length})</p>
          {invites.map((i) => (
            <div key={i.invite_id} className="flex flex-wrap items-center justify-between gap-3 border border-border rounded-lg px-3 py-2 text-sm" data-testid={`invite-row-${i.invite_id}`}>
              <div className="flex-1 min-w-[180px]">
                <p className="font-medium">{i.name}</p>
                <p className="text-foreground/60 text-xs">{i.email} · {i.admin_level_label}</p>
              </div>
              <Badge className={STATUS_STYLES[i.status] || ''} data-testid={`invite-status-${i.invite_id}`}>
                {STATUS_LABELS[i.status] || i.status}
              </Badge>
              {canManage && i.status !== 'accepted' && (
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => resend(i.invite_id)} data-testid={`invite-resend-${i.invite_id}`}>
                    <ArrowClockwise size={14} className="mr-1" /> Rinvia
                  </Button>
                  <Button size="sm" variant="ghost" className="rounded-full h-8 text-red-600" onClick={() => revoke(i.invite_id)} data-testid={`invite-revoke-${i.invite_id}`}>
                    <Trash size={14} className="mr-1" /> Revoca
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminAccountsPanel;
